import '../../../../assets/css/App.css';
import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import {
   Flex,
   Text,
   useColorModeValue,
   Box,
   GridItem,
   Input,
   Popover,
   PopoverTrigger,
   PopoverContent,
   PopoverBody,
   Button,
   Image,
   Drawer,
   DrawerOverlay,
   DrawerContent,
   DrawerCloseButton,
   DrawerBody,
   DrawerHeader,
   useDisclosure,
   Table,
   Thead,
   Tbody,
   Tr,
   Th,
   Td,
   Card,
   Grid,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   HStack,
   PinInput,
   PinInputField,
   VStack,
   useToast,
   Spinner,
   Center,
   InputGroup,
   InputLeftElement,
   Icon,
   Select,
   IconButton,
   useColorMode,
} from '@chakra-ui/react';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { SearchIcon, CloseIcon, RepeatIcon } from '@chakra-ui/icons';

import { renderDynamicDropdown } from 'components/ui/DynamicDropdown';
import CompanyForm from '../../companyProfile/CompanyForm';
import CompanySettingForm from '../../companyProfile/CompanySettingForm';

import { useGlobal, useUser, useDaybook } from '../../../../contexts';
import { getGSTR2AB2BReconcilation } from '../../../../redux/actions/reports';
import { getGstAuth } from '../../../../redux/actions/settings';

import back from '../../../../../src/assets/img/nfts/back.svg';
import download from '../../../../assets/img/nfts/download.svg';
import xls from '../../../../../src/assets/img/nfts/xls.svg';
import json from '../../../../../src/assets/img/nfts/json.svg';
import dot from '../../../../assets/img/nfts/dot.svg';
import nodata from 'assets/img/nfts/no data.svg';
import SalesDetails from 'components/salesDetails/SalesDetails';
import { deleteRecord } from 'utils/util';
import { deleteRecordForSales } from 'utils/util';
import { deletePurchaseReturnInvoice } from '../../../../redux/actions/purchaseReturnInvoice';
import { deleteSaleReturnInvoice } from '../../../../redux/actions/salesReturnInvoice';
import { deleteSaleInvoice } from '../../../../redux/actions/salesInvoice';
import {
   deletePurchaseInvoice,
   getSinglePurchaseWithPrevAndNext,
   importFromGst,
} from '../../../../redux/actions/purchaseInvoice';
import { BsFileEarmarkText } from 'react-icons/bs';

// Constants
const TABLE_COLUMNS_CONFIG = [
   { key: 'accountGst', header: 'GSTIN', size: '150px', minSize: '120px' },
   { key: 'accountname', header: 'Supplier Name', size: '150px', minSize: '170px' },
   { key: 'daybookname', header: 'Daybook Name', size: '150px', minSize: '170px' },
   { key: 'partyNo', header: 'Bill No', size: '100px', minSize: '100px' },
   { key: 'transactionAt', header: 'Bill Date', size: '120px', minSize: '120px' },
   { key: 'taxableAmount', header: 'Taxable Amount', size: '150px', minSize: '120px' },
   {
      key: 'total_cgst',
      header: 'CGST',
      size: '60px',
      minSize: '60px',
   },
   {
      key: 'total_sgst',
      header: 'SGST',
      size: '60px',
      minSize: '60px',
   },
   {
      key: 'total_igst',
      header: 'IGST',
      size: '60px',
      minSize: '60px',
   },
   { key: 'gstAmount', header: 'Tax Amount', size: '150px', minSize: '120px' },
   { key: 'error', header: 'Error', size: '150px', minSize: '120px' },
   { key: 'xBillNo', header: 'X Bill No', size: '150px', minSize: '120px' },
   { key: 'xBillDate', header: 'X Bill Date', size: '150px', minSize: '120px' },
   { key: 'xTaxableValue', header: 'X Taxable Amount', size: '150px', minSize: '120px' },
   { key: 'xcgst', header: 'xCGST', size: '60px', minSize: '60px' },
   { key: 'xsgst', header: 'xSGST', size: '60px', minSize: '60px' },
   { key: 'xigst', header: 'xIGST', size: '60px', minSize: '60px' },
   { key: 'xTaxAmount', header: 'X Tax Amount', size: '150px', minSize: '120px' },
];

// Filter options
const FILTER_OPTIONS = [
   { value: 'all', label: 'All' },
   { value: 'match', label: 'Match' },
   { value: 'mismatch', label: 'Mismatch' },
   { value: 'extra_found', label: 'Extra Found' },
   { value: 'not_found', label: 'Not Found in Portal' },
];

// Search related constants
const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 2;

// Date utility functions
const formatDateForGST = (dateString) => {
   if (!dateString) return '';

   try {
      const date = moment(dateString, 'YYYY-MM', true);
      if (!date.isValid()) {
         console.error('Invalid date format:', dateString);
         return '';
      }

      const month = date.format('MM');
      const year = date.format('YYYY');
      return `${month}${year}`;
   } catch (error) {
      console.error('Error formatting date for GST:', error);
      return '';
   }
};

const isValidDate = (dateString) => {
   if (!dateString) return false;
   return moment(dateString, 'YYYY-MM', true).isValid();
};
const getPreviousMonth = () => {
   return moment().subtract(1, 'month').format('YYYY-MM');
};

// Custom hooks for better separation of concerns
const useGSTR2AReport = () => {
   const dispatch = useDispatch();
   const toast = useToast();
   const { user } = useUser();
   const { companyList } = useGlobal();

   const [state, setState] = useState({
      companyFilter: [],
      fromDate: getPreviousMonth(),
      toDate: new Date().toISOString().split('T')[0],
      isSendOtp: false,
      otp: null,
      isLoading: false,
      searchQuery: '',
      statusFilter: 'all',
      isInitialized: false,
      isRefreshing: false,
   });

   const GSTR2AB2BReconcilation = useSelector((state) => state.reports?.GSTR2AB2BReconcilation || []);

   const updateState = useCallback((updates) => setState((prev) => ({ ...prev, ...updates })), []);

   const companyOptions = useMemo(
      () =>
         companyList
            ?.filter((c) => !c.dbName)
            .map((comp) => ({
               value: comp.id,
               label: comp.name,
               id: comp.id,
               name: comp.name,
            })) || [],
      [companyList],
   );

   return {
      state,
      updateState,
      GSTR2AB2BReconcilation,
      companyOptions,
      dispatch,
      toast,
      user,
   };
};

const useModalControls = () => {
   const mainDrawer = useDisclosure();
   const otpModal = useDisclosure();
   const companyModal = useDisclosure();
   const companySettingModal = useDisclosure();

   return {
      mainDrawer,
      otpModal,
      companyModal,
      companySettingModal,
   };
};

// Helper function to determine record status
const getRecordStatus = (record) => {
   if (record.error) {
      const errorLower = record.error.toLowerCase();
      // Check for 'mismatch' BEFORE checking for 'match'
      if (errorLower.includes('mismatch')) {
         return 'mismatch';
      } else if (errorLower.includes('match') || errorLower.includes('matched')) {
         return 'match';
      } else if (errorLower.includes('extra') || errorLower.includes('extra found')) {
         return 'extra_found';
      } else if (errorLower.includes('not found') || errorLower.includes('not found in portal')) {
         return 'not_found';
      }
   }

   // Fallback logic for records without explicit error messages
   if (record.xBillNo && record.xBillDate) {
      if (
         record.partyNo === record.xBillNo &&
         moment(record.transactionAt).format('YYYY-MM-DD') === moment(record.xBillDate).format('YYYY-MM-DD') &&
         Math.abs(record.taxableAmount - record.xTaxableValue) < 0.01 &&
         Math.abs(record.gstAmount - record.xTaxAmount) < 0.01
      ) {
         return 'match';
      } else {
         return 'mismatch';
      }
   }

   if (record.xBillNo && !record.partyNo) {
      return 'extra_found';
   }

   if (record.partyNo && !record.xBillNo) {
      return 'not_found';
   }

   return 'mismatch';
};

const useSearchAndFilter = (data, searchQuery, statusFilter) => {
   return useMemo(() => {
      let filteredData = data;

      if (statusFilter && statusFilter !== 'all') {
         filteredData = data.filter((item) => {
            const recordStatus = getRecordStatus(item);
            return recordStatus === statusFilter;
         });
      }

      if (!searchQuery || searchQuery.length < MIN_SEARCH_LENGTH) {
         return filteredData;
      }

      const query = searchQuery.toLowerCase();
      return filteredData.filter((item) =>
         Object.keys(item).some((key) => {
            const value = item[key];
            if (typeof value === 'string' || typeof value === 'number') {
               return value.toString().toLowerCase().includes(query);
            }
            return false;
         }),
      );
   }, [data, searchQuery, statusFilter]);
};

const TableColumns = () =>
   useMemo(
      () =>
         TABLE_COLUMNS_CONFIG.map(({ key, header, size, minSize, accessorFn }) => {
            const columnDef = {
               id: key,
               header,
               cell: (info) => <span>{info.getValue()}</span>,
               size,
               minSize,
            };

            if (accessorFn) {
               columnDef.accessorFn = accessorFn;
            } else {
               columnDef.accessorKey = key;
            }

            return columnDef;
         }),
      [],
   );

const SearchBar = ({ searchQuery, onSearchChange, onClearSearch, placeholder = 'Search across all columns...' }) => (
   <InputGroup size="md" width={{ base: '100%', md: '300px' }}>
      <InputLeftElement pointerEvents="none">
         <Icon as={SearchIcon} color="gray.400" />
      </InputLeftElement>
      <Input
         placeholder={placeholder}
         value={searchQuery}
         onChange={onSearchChange}
         bg="white"
         _dark={{ bg: 'gray.700' }}
      />
      {searchQuery && (
         <InputLeftElement right="0" onClick={onClearSearch} cursor="pointer">
            <Icon as={CloseIcon} color="gray.400" _hover={{ color: 'gray.600' }} />
         </InputLeftElement>
      )}
   </InputGroup>
);

const StatusFilter = ({ statusFilter, onStatusFilterChange }) => (
   <Box zIndex={3} width={'200px'} fontSize={'13px'} fontWeight={500} mr={2}>
      <label>Filter by Status</label>
      <Select
         value={statusFilter}
         onChange={onStatusFilterChange}
         placeholder="Select Status"
         bg="white"
         _dark={{ bg: 'gray.700' }}
         size="md">
         {FILTER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
               {option.label}
            </option>
         ))}
      </Select>
   </Box>
);

const DownloadMenu = ({ onDownloadExcel, onDownloadJSON }) => (
   <Popover>
      <PopoverTrigger>
         <Image src={download} alt="Download" width="25px" cursor="pointer" _hover={{ opacity: 0.8 }} />
      </PopoverTrigger>
      <PopoverContent width="220px" zIndex="10" mr={8}>
         <PopoverBody p="3">
            <Button
               width="100%"
               variant="ghost"
               _hover={{ bg: 'blue.100' }}
               onClick={onDownloadExcel}
               justifyContent="space-between"
               mb={3}>
               <Image src={xls} alt="Download XLS" width="30px" />
               Download Excel
            </Button>
            <Button
               width="100%"
               variant="ghost"
               _hover={{ bg: 'blue.100' }}
               onClick={onDownloadJSON}
               justifyContent="space-between">
               <Image src={json} alt="Download JSON" width="30px" />
               Download JSON
            </Button>
         </PopoverBody>
      </PopoverContent>
   </Popover>
);

const RefreshButton = ({ onRefresh, isLoading, isRefreshing }) => (
   <Button
      onClick={onRefresh}
      isLoading={isLoading || isRefreshing}
      // loadingText="Refreshing"
      // leftIcon={<RepeatIcon />}
      colorScheme="blue"
      variant="outline"
      size="md"
      _hover={{ bg: 'blue.50' }}
      transition="all 0.2s">
      <RepeatIcon />
   </Button>
);

const OTPSection = ({ otp, onOtpChange, isDisabled, email }) => (
   <VStack spacing={6} py={4}>
      <Text fontSize="lg" fontWeight="600" color="gray.700" textAlign="center">
         Enter 6-digit OTP
      </Text>
      <HStack justifyContent="center" spacing={3}>
         <PinInput otp value={otp} onChange={onOtpChange} size="lg" placeholder="">
            {Array.from({ length: 6 }).map((_, index) => (
               <PinInputField
                  key={index}
                  w="50px"
                  h="50px"
                  fontSize="xl"
                  fontWeight="600"
                  borderRadius="12px"
                  borderWidth="2px"
                  borderColor="green.200"
                  bg="green.50"
                  _focus={{
                     borderColor: 'green.400',
                     bg: 'white',
                     boxShadow: '0 0 0 1px var(--chakra-colors-green-400)',
                  }}
                  _hover={{
                     borderColor: 'green.300',
                  }}
               />
            ))}
         </PinInput>
      </HStack>
      <Text fontSize="sm" color="gray.500" textAlign="center">
         OTP sent to: {email || 'your registered email'}
      </Text>
   </VStack>
);

const NoDataState = ({ hasFilters }) => {
   const columns = TableColumns();

   return (
      <Tr>
         <Td colSpan={columns.length}>
            <Center py={10}>
               <VStack spacing={4}>
                  <Image src={nodata} alt="No Data Found" width="200px" height="200px" opacity={0.7} />
                  <Text color="gray.500" fontSize="lg">
                     {hasFilters ? 'No records found for the selected filters' : 'No GSTR2A reconciliation data found'}
                  </Text>
               </VStack>
            </Center>
         </Td>
      </Tr>
   );
};

const GSTR2AB2BReconcilation = () => {
   const { state, updateState, GSTR2AB2BReconcilation, companyOptions, dispatch, toast, user } = useGSTR2AReport();
   const filteredData = useSearchAndFilter(GSTR2AB2BReconcilation, state.searchQuery, state.statusFilter);
   const { mainDrawer, otpModal, companyModal, companySettingModal } = useModalControls();
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const fromDateInputRef = useRef(null);
   const searchTimeoutRef = useRef(null);
   const fetchTimeoutRef = useRef(null);
   const lastFetchedRef = useRef(null);
   const isFetchingRef = useRef(false);
   const { userGlobalSettingsData } = useGlobal();

   const textColor = useColorModeValue('secondaryGray.900', 'white');
   const backgroundColor = useColorModeValue('white', '#101F4A');
   const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
   const [isOpenPurchaseReturnSingleView, setIsOpenPurchaseReturnSingleView] = React.useState(false);
   const [entityType, setEntityType] = useState('');
   const [isEditFormOpen, setIsEditFormOpen] = React.useState(false);
   const [selectedRowId, setSelectedRowId] = useState(null);

   const companyId = Number(localStorage.getItem('companyId'));
   const userRights = user?.user?.userRights || user?.userRights;
   const purchaseInvoiceRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.purchaseinvoice
      : null;
   const purchaseReturnRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.purchasereturn
      : null;

   const viewData = useSelector((state) => state?.purchaseInvoice?.purchaseData);
   const { viewDetail, paginate } = useMemo(() => {
      const data = viewData?.data[0];

      const pagination = viewData?.pagination;

      return { viewDetail: data, paginate: pagination };
   }, [viewData?.data, viewData?.pagination]);

   // Memoized table instance
   const table = useReactTable({
      data: filteredData,
      columns: TableColumns(),
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });

   // Handle row click to open single view
   const handleRowClick = useCallback(
      async (row) => {
         const rowData = row.original;

         if (!rowData.id) return;

         try {
            setEntityType('purchaseinvoice');
            await dispatch(
               getSinglePurchaseWithPrevAndNext({
                  id: rowData.id,
                  page: 1,
                  limit: 1,
                  sortBy: 'id',
                  sortOrder: 'desc',
               }),
            );
            setIsOpenPurchaseReturnSingleView(true);
         } catch (error) {
            console.error('Error loading record:', error);
            toast({
               title: 'Error Loading Record',
               description: error.message || 'Failed to load record details',
               status: 'error',
               duration: 3000,
               isClosable: true,
            });
         }
      },
      [companyId, dispatch, toast],
   );

   const { purchaseDaybooks: daybookData } = useDaybook();
   const { isOpen, onOpen, onClose } = useDisclosure();
   const [selectedRowData, setSelectedRowData] = useState(null);
   const [selectedRowDaybook, setSelectedRowDaybook] = useState(null);

   const handleActionClick = async (value) => {
      console.log('Action Clicked', value);
      onOpen();
      setSelectedRowData(value?.original);
   };

   const handleActionClose = () => {
      setSelectedRowDaybook(null);
      onClose();
   };

   const handleSavePurchase = async () => {
      if (!selectedRowDaybook) {
         toast.error('Please select a daybook');
         return;
      }

      delete selectedRowData.error;
      delete selectedRowData.matched;
      delete selectedRowData.status;
      delete selectedRowData.color;
      delete selectedRowData.xcess;
      delete selectedRowData.orgGstData.status;
      delete selectedRowData.orgGstData.color;
      delete selectedRowData.orgGstData.error;
      delete selectedRowData.orgGstData.itms[0].itm_det.csamt;

      const payload = {
         companyId: user?.loginhistory?.companyId,
         daybookId: Number(selectedRowDaybook),
         gstData: selectedRowData,
      };

      const res = await dispatch(importFromGst(payload));
      if (res && res.code === 1) {
         onClose();
         setSelectedRowDaybook(null);
      }
      console.log('Purchase Payload:', payload, res);
   };

   // Search handlers
   const handleSearchChange = useCallback(
      (e) => {
         const value = e.target.value;
         updateState({ searchQuery: value });

         if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
         }

         searchTimeoutRef.current = setTimeout(() => {
            // Search logic handled in useSearchAndFilter
         }, DEBOUNCE_DELAY);
      },
      [updateState],
   );

   const handleClearSearch = useCallback(() => {
      updateState({ searchQuery: '' });
      if (searchTimeoutRef.current) {
         clearTimeout(searchTimeoutRef.current);
      }
   }, [updateState]);

   // Status filter handler
   const handleStatusFilterChange = useCallback(
      (e) => {
         updateState({ statusFilter: e.target.value });
      },
      [updateState],
   );

   // Refresh handler
   const handleRefresh = useCallback(async () => {
      const companyId = user?.loginhistory?.companyId;
      if (!companyId || !state.fromDate) {
         return;
      }

      if (!isValidDate(state.fromDate)) {
         toast({
            title: 'Invalid Date',
            description: 'Please select a valid month and year',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         return;
      }

      updateState({ isRefreshing: true });

      try {
         // Clear previous data
         dispatch({ type: 'CLEAR_GSTR2A_RECONCILIATION' });

         // Clear the last fetched cache to force a fresh fetch
         lastFetchedRef.current = null;

         const returnPeriod = formatDateForGST(state.fromDate);
         if (!returnPeriod) {
            throw new Error('Failed to format date for GST API');
         }

         const res = await dispatch(
            getGSTR2AB2BReconcilation({
               returnPeriod: returnPeriod,
               companyId: companyId,
            }),
         );

         lastFetchedRef.current = `${state.fromDate}-${companyId}`;

         if (res?.code === 'EDIT_COMPANY') {
            companyModal.onOpen();
         } else if (res?.code === 'EDIT_COMPANY_SETTING') {
            companySettingModal.onOpen();
         } else if (res?.code === 'GST_OTP') {
            updateState({ isSendOtp: true });
            otpModal.onOpen();
         }

         if (res?.error) {
            toast({
               title: 'Error refreshing report',
               description: res.message || 'Failed to refresh GSTR2A data',
               status: 'error',
               duration: 3000,
               isClosable: true,
            });
         } else {
            toast({
               title: 'Refreshed',
               description: 'GSTR2A data has been refreshed successfully',
               status: 'success',
               duration: 2000,
               isClosable: true,
            });
         }
      } catch (error) {
         console.error('Refresh error:', error);
         toast({
            title: 'Refresh Failed',
            description: error.message || 'Failed to refresh data',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      } finally {
         updateState({ isRefreshing: false });
      }
   }, [
      state.fromDate,
      user?.loginhistory?.companyId,
      dispatch,
      updateState,
      toast,
      companyModal,
      companySettingModal,
      otpModal,
   ]);

   const storedCompanyId = localStorage.getItem('companyId');
   const deletePurchaseReturn = () => {
      if (entityType === 'purchasereturn') {
         deleteRecord(
            viewDetail?.id,
            deletePurchaseReturnInvoice,
            dispatch,
            user?.loginhistory?.companyId,
            userGlobalSettingsData,
         );
      }
      if (entityType === 'salereturn') {
         deleteRecord(
            viewDetail?.id,
            deleteSaleReturnInvoice,
            dispatch,
            user?.loginhistory?.companyId,
            userGlobalSettingsData,
         );
      }
      if (entityType === 'saleinvoice') {
         deleteRecord(
            viewDetail?.id,
            deleteSaleInvoice,
            dispatch,
            user?.loginhistory?.companyId,
            userGlobalSettingsData,
         );
         deleteRecordForSales(
            viewDetail?.id,
            viewDetail?.daybookId,
            viewDetail?.accountId,
            deleteSaleInvoice,
            dispatch,
            storedCompanyId,
            userGlobalSettingsData,
         );
      }
      if (entityType === 'purchaseinvoice') {
         deleteRecord(
            viewDetail?.id,
            deletePurchaseInvoice,
            dispatch,
            user?.loginhistory?.companyId,
            userGlobalSettingsData,
         );
         deleteRecordForSales(
            viewDetail?.id,
            viewDetail?.daybookId,
            viewDetail?.accountId,
            deletePurchaseInvoice,
            dispatch,
            storedCompanyId,
            userGlobalSettingsData,
         );
      }
      setIsOpenPurchaseReturnSingleView(false);
   };

   const handleSalesDetailsClose = () => {
      setIsOpenPurchaseReturnSingleView(false);
      setSelectedRowId(null);
      setEntityType('');
   };

   const handleNextPage = () => {
      console.log('Next');
   };
   const handlePrevPage = () => {
      console.log('Prev');
   };

   // Date change handler with validation
   const handleDateChange = useCallback(
      (e) => {
         const newDate = e.target.value;

         if (!isValidDate(newDate)) {
            toast({
               title: 'Invalid Date',
               description: 'Please select a valid month and year',
               status: 'error',
               duration: 3000,
               isClosable: true,
            });
            return;
         }

         updateState({ fromDate: newDate });
      },
      [updateState, toast],
   );

   // Event handlers
   const handleCompanyChange = useCallback(
      (selectedOptions) => {
         updateState({ companyFilter: selectedOptions || [] });
      },
      [updateState],
   );

   const handleSendOtp = useCallback(async () => {
      if (!user?.loginhistory?.companyId) return;

      try {
         await dispatch(getGstAuth({ companyId: user.loginhistory.companyId }));
         updateState({ isSendOtp: false });
         toast({
            title: 'OTP Sent',
            description: 'Check your registered email for the OTP',
            status: 'success',
            duration: 3000,
            isClosable: true,
         });
      } catch (error) {
         toast({
            title: 'Error',
            description: 'Failed to send OTP',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   }, [dispatch, user, toast, updateState]);

   const handleVerifyOtp = useCallback(async () => {
      if (!state.otp || state.otp.length !== 6) return;

      try {
         const res = await dispatch(
            getGstAuth({
               companyId: user?.loginhistory?.companyId,
               otp: state.otp,
            }),
         );

         if (res?.code === 1) {
            otpModal.onClose();
            updateState({ otp: null });

            lastFetchedRef.current = null;

            isFetchingRef.current = true;
            try {
               updateState({ isLoading: true });

               const returnPeriod = formatDateForGST(state.fromDate);
               if (!returnPeriod) {
                  throw new Error('Invalid date format');
               }

               await dispatch(
                  getGSTR2AB2BReconcilation({
                     returnPeriod: returnPeriod,
                     companyId: user.loginhistory.companyId,
                  }),
               );

               lastFetchedRef.current = `${state.fromDate}-${user.loginhistory.companyId}`;
            } finally {
               updateState({ isLoading: false });
               isFetchingRef.current = false;
            }

            toast({
               title: 'Success',
               description: 'OTP verified successfully',
               status: 'success',
               duration: 3000,
               isClosable: true,
            });
         }
      } catch (error) {
         console.error('OTP verification error:', error);
         toast({
            title: 'Error',
            description: error.message || 'Invalid OTP',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   }, [dispatch, state.otp, state.fromDate, user, otpModal, toast, updateState]);

   const handleDownloadExcel = useCallback(() => {
      toast({
         title: 'Feature Coming Soon',
         description: 'Excel download functionality will be available soon',
         status: 'info',
         duration: 3000,
         isClosable: true,
      });
   }, [toast]);

   const handleDownloadJSON = useCallback(() => {
      const jsonData = JSON.stringify(filteredData, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gstr2a-report-${state.fromDate}-${state.statusFilter}.json`;
      a.click();
      URL.revokeObjectURL(url);
   }, [filteredData, state.fromDate, state.statusFilter]);

   // Effects
   useEffect(() => {
      if (!user?.packageData?.[0]?.is2a2b) {
         window.history.back();
      }
   }, [user]);

   useEffect(() => {
      if (user?.loginhistory?.companyId && companyOptions.length > 0) {
         const company = companyOptions.find((x) => x.id === user.loginhistory.companyId);
         if (company && state.companyFilter.length === 0) {
            updateState({ companyFilter: [company.id] });
         }
      }
   }, [companyOptions, user?.loginhistory?.companyId, state.companyFilter.length, updateState]);

   useEffect(() => {
      const previousMonth = getPreviousMonth();
      lastFetchedRef.current = null;
      dispatch({ type: 'CLEAR_GSTR2A_RECONCILIATION' });
      updateState({
         fromDate: previousMonth,
         isInitialized: true,
      });
   }, [dispatch, updateState]);

   // Main data fetching effect
   useEffect(() => {
      const companyId = user?.loginhistory?.companyId;
      if (!state.isInitialized || !companyId || !state.fromDate) {
         return;
      }

      if (!isValidDate(state.fromDate)) {
         console.error('Invalid date format:', state.fromDate);
         return;
      }

      const fetchKey = `${state.fromDate}-${companyId}`;
      if (lastFetchedRef.current === fetchKey || isFetchingRef.current) {
         return;
      }

      if (fetchTimeoutRef.current) {
         clearTimeout(fetchTimeoutRef.current);
      }

      fetchTimeoutRef.current = setTimeout(async () => {
         isFetchingRef.current = true;
         updateState({ isLoading: true });

         try {
            const returnPeriod = formatDateForGST(state.fromDate);
            if (!returnPeriod) {
               throw new Error('Failed to format date for GST API');
            }

            const res = await dispatch(
               getGSTR2AB2BReconcilation({
                  returnPeriod: returnPeriod,
                  companyId: companyId,
               }),
            );

            lastFetchedRef.current = fetchKey;

            if (res?.code === 'EDIT_COMPANY') {
               companyModal.onOpen();
            } else if (res?.code === 'EDIT_COMPANY_SETTING') {
               companySettingModal.onOpen();
            } else if (res?.code === 'GST_OTP') {
               updateState({ isSendOtp: true });
               otpModal.onOpen();
            }

            if (res?.error) {
               toast({
                  title: 'Error fetching report',
                  description: res.message || 'Failed to load GSTR2A data',
                  status: 'error',
                  duration: 3000,
                  isClosable: true,
               });
            }
         } catch (error) {
            console.error('Fetch error:', error);
            toast({
               title: 'Error',
               description: error.message || 'Failed to fetch report data',
               status: 'error',
               duration: 3000,
               isClosable: true,
            });
         } finally {
            updateState({ isLoading: false });
            isFetchingRef.current = false;
         }
      }, 500);

      return () => {
         if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
         }
      };
   }, [
      state.fromDate,
      user?.loginhistory?.companyId,
      state.isInitialized,
      dispatch,
      companyModal,
      companySettingModal,
      otpModal,
      updateState,
      toast,
   ]);

   useEffect(() => {
      return () => {
         if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
         }
         if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
         }
         isFetchingRef.current = false;
         lastFetchedRef.current = null;
      };
   }, []);

   const getEnhancedRowStyles = (backendColor) => {
      if (!backendColor) return { backgroundColor: 'transparent', color: 'inherit' };

      const hexToRgba = (hex, alpha = 0.1) => {
         hex = hex.replace('#', '');
         const r = parseInt(hex.substring(0, 2), 16);
         const g = parseInt(hex.substring(2, 4), 16);
         const b = parseInt(hex.substring(4, 6), 16);
         return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      return {
         backgroundColor: hexToRgba(backendColor, 0.15),
         color: backendColor,
         borderLeft: `4px solid ${backendColor}`,
      };
   };

   // Render components
   const renderHeader = () => (
      <Flex pr="25px" mb="5px" justifyContent="space-between" align="center" mt="50px">
         <Flex align="center" px={3} mt={2}>
            <Image
               src={back}
               alt="Go Back"
               width="15px"
               cursor="pointer"
               onClick={() => window.history.back()}
               mr={5}
               _hover={{ transform: 'translateX(-2px)' }}
               transition="transform 0.2s"
            />
            <Text color={textColor} fontSize="15px" fontWeight="700" lineHeight="100%">
               GSTR 2A/B2B RECONCILIATION
            </Text>
         </Flex>
      </Flex>
   );

   const renderFilters = () => (
      <Box mt="15px" bg={backgroundColor} px={4} py={0} borderRadius="7px">
         <Flex alignItems="end" justifyContent={{ base: 'end', lg: 'end' }} gap={4} py={3}>
            <Flex align="center" gap={3}>
               <Box display={{ base: 'none', xl: 'block' }} flex="">
                  <VStack spacing={3} align="stretch">
                     <Flex justifyContent="center" alignItems="center" gap={5}>
                        <Box>
                           <Text fontSize="13px" fontWeight="500" mb={1}>
                              Return Period
                           </Text>
                           <Input
                              id="return-period"
                              type="month"
                              value={state.fromDate}
                              onChange={handleDateChange}
                              ref={fromDateInputRef}
                              w="180px"
                              h="30px"
                              isInvalid={!isValidDate(state.fromDate)}
                           />
                        </Box>
                        <StatusFilter
                           statusFilter={state.statusFilter}
                           onStatusFilterChange={handleStatusFilterChange}
                        />
                        <Box flex="1" minW="300px">
                           <SearchBar
                              searchQuery={state.searchQuery}
                              onSearchChange={handleSearchChange}
                              onClearSearch={handleClearSearch}
                              placeholder="Search here..."
                           />
                        </Box>
                     </Flex>
                  </VStack>
               </Box>
               <RefreshButton onRefresh={handleRefresh} isLoading={state.isLoading} isRefreshing={state.isRefreshing} />
               <DownloadMenu onDownloadExcel={handleDownloadExcel} onDownloadJSON={handleDownloadJSON} />

               <Box display={{ base: 'block', xl: 'none' }}>
                  <Button variant="unstyled" onClick={mainDrawer.onOpen}>
                     <Image src={dot} alt="Menu" width="25px" />
                  </Button>
               </Box>
            </Flex>
         </Flex>
      </Box>
   );

   const renderMobileDrawer = () => (
      <Drawer isOpen={mainDrawer.isOpen} placement="bottom" onClose={mainDrawer.onClose}>
         <DrawerOverlay />
         <DrawerContent borderTopRadius="20px">
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px">Filters</DrawerHeader>
            <DrawerBody>
               <VStack spacing={4} py={4}>
                  <Box width="100%">
                     <Text fontSize="13px" fontWeight="500" mb={1}>
                        Return Period
                     </Text>
                     <Input
                        id="mobile-return-period"
                        type="month"
                        value={state.fromDate}
                        onChange={handleDateChange}
                        w="100%"
                        h="40px"
                        isInvalid={!isValidDate(state.fromDate)}
                     />
                  </Box>

                  <Box width="100%">
                     <StatusFilter statusFilter={state.statusFilter} onStatusFilterChange={handleStatusFilterChange} />
                  </Box>

                  <Button
                     colorScheme="blue"
                     width="100%"
                     onClick={handleRefresh}
                     isLoading={state.isLoading || state.isRefreshing}
                     leftIcon={<RepeatIcon />}></Button>

                  <Button colorScheme="gray" width="100%" onClick={mainDrawer.onClose} mt={2}>
                     Apply Filters
                  </Button>
               </VStack>
            </DrawerBody>
         </DrawerContent>
      </Drawer>
   );

   const renderTable = () => (
      <Grid overflow="auto">
         <Card mt="15px" borderRadius="7px" overflow="hidden" bg={isDark ? '#101F4A' : 'white'}>
            <Box
               overflowX="auto"
               className="custom-scrollbar"
               position="relative"
               minH={{ base: '70vh', md: '70vh', xl: '75vh' }}
               maxH={{ base: '70vh', md: '70vh', xl: '75vh' }}>
               {state.isLoading ? (
                  <Center height="400px">
                     <VStack spacing={4}>
                        <Spinner size="xl" color={isDark ? 'blue.400' : 'blue.500'} />
                        <Text color={isDark ? 'gray.400' : 'gray.500'}>Loading report data...</Text>
                     </VStack>
                  </Center>
               ) : (
                  <Table variant="simple">
                     <Thead position="sticky" top={0} zIndex={1}>
                        {table.getHeaderGroups().map((headerGroup) => (
                           <Tr key={headerGroup.id}>
                              <Th
                                 borderColor={isDark ? 'gray.700' : borderColor}
                                 cursor="pointer"
                                 color={isDark ? 'gray.300' : 'gray.600'}>
                                 <Flex
                                    justifyContent="space-between"
                                    align="center"
                                    fontSize={{ sm: '10px', lg: '12px' }}>
                                    <Box>Action</Box>
                                 </Flex>
                              </Th>
                              {headerGroup.headers.map((header) => (
                                 <Th
                                    key={header.id}
                                    colSpan={header.colSpan}
                                    borderColor={isDark ? 'gray.700' : borderColor}
                                    cursor="pointer"
                                    width={header.column.columnDef.size}
                                    minW={header.column.columnDef.minSize}
                                    onClick={header.column.getToggleSortingHandler()}
                                    color={isDark ? 'gray.300' : 'gray.600'}>
                                    <Flex
                                       justifyContent="space-between"
                                       align="center"
                                       fontSize={{ sm: '10px', lg: '12px' }}>
                                       <Box>{flexRender(header.column.columnDef.header, header.getContext())}</Box>
                                       {{
                                          asc: '↑',
                                          desc: '↓',
                                       }[header.column.getIsSorted()] ?? null}
                                    </Flex>
                                 </Th>
                              ))}
                           </Tr>
                        ))}
                     </Thead>
                     <Tbody>
                        {filteredData.length === 0 ? (
                           <NoDataState hasFilters={state.statusFilter !== 'all' || state.searchQuery !== ''} />
                        ) : (
                           table.getRowModel().rows.map((row) => {
                              const hasDocId = row?.original?.docid;
                              return (
                                 <Tr
                                    key={row.id}
                                    style={getEnhancedRowStyles(row?.original?.color)}
                                    onClick={() => handleRowClick(row)}
                                    cursor={hasDocId ? 'pointer' : 'default'}
                                    _hover={{
                                       opacity: 0.9,
                                       backgroundColor: hasDocId ? (isDark ? 'gray.700' : 'gray.50') : 'transparent',
                                    }}
                                    transition="all 0.2s ease">
                                    {row?.original?.color === '#5900ff8c' ? (
                                       <Td borderColor={isDark ? 'gray.700' : 'gray.200'}>
                                          <IconButton
                                             aria-label="View Document"
                                             icon={<BsFileEarmarkText />}
                                             size="sm"
                                             variant="ghost"
                                             colorScheme="blue"
                                             onClick={() => handleActionClick(row)}
                                             color={isDark ? 'blue.300' : 'blue.500'}
                                             _hover={{
                                                bg: isDark ? 'gray.700' : 'gray.100',
                                             }}
                                          />
                                       </Td>
                                    ) : (
                                       <Td borderColor={isDark ? 'gray.700' : 'gray.200'}></Td>
                                    )}
                                    {row.getVisibleCells().map((cell) => (
                                       <Td
                                          key={cell.id}
                                          fontSize={{ sm: '14px' }}
                                          borderColor={isDark ? 'gray.700' : 'gray.200'}
                                          fontWeight="600"
                                          color={isDark ? 'gray.200' : 'gray.800'}>
                                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                       </Td>
                                    ))}
                                 </Tr>
                              );
                           })
                        )}
                     </Tbody>
                  </Table>
               )}
            </Box>
         </Card>
      </Grid>
   );

   const renderOtpModal = () => (
      <Modal isOpen={otpModal.isOpen} onClose={otpModal.onClose}>
         <ModalOverlay />
         <ModalContent>
            <ModalHeader>GST Authentication</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
               {state.isSendOtp ? (
                  <VStack spacing={4}>
                     <Text>Click below to receive OTP for GST authentication</Text>
                     <Button colorScheme="green" onClick={handleSendOtp} width="100%">
                        Send OTP
                     </Button>
                  </VStack>
               ) : (
                  <OTPSection
                     otp={state.otp}
                     onOtpChange={(value) => updateState({ otp: value })}
                     isDisabled={!state.otp || state.otp.length !== 6}
                  />
               )}
            </ModalBody>
            <ModalFooter>
               <Button variant="outline" mr={3} onClick={otpModal.onClose}>
                  Cancel
               </Button>
               {!state.isSendOtp && (
                  <Button
                     colorScheme="green"
                     onClick={handleVerifyOtp}
                     isDisabled={!state.otp || state.otp.length !== 6}>
                     Verify OTP
                  </Button>
               )}
            </ModalFooter>
         </ModalContent>
      </Modal>
   );

   return (
      <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
         {renderHeader()}
         {renderFilters()}
         {renderTable()}
         {renderMobileDrawer()}
         {renderOtpModal()}

         <CompanyForm
            isOpen={companyModal.isOpen}
            onClose={companyModal.onClose}
            isEditMode={true}
            currentRow={companyOptions.find((c) => c.id === user?.loginhistory?.companyId)}
         />

         <CompanySettingForm
            isOpen={companySettingModal.isOpen}
            onClose={companySettingModal.onClose}
            isEditMode={true}
            currentRow={companyOptions.find((c) => c.id === user?.loginhistory?.companyId)}
         />
         <Modal isOpen={isOpen} onClose={handleActionClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="xl">
               <ModalHeader>Add Purchase</ModalHeader>
               <ModalCloseButton />

               <ModalBody>
                  <VStack spacing={4} align="stretch">
                     {/* Daybook Dropdown */}
                     <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>
                           Select Daybook
                        </Text>
                        <Select
                           placeholder="Select daybook"
                           value={selectedRowDaybook}
                           onChange={(e) => setSelectedRowDaybook(e.target.value)}>
                           {daybookData.map((daybook) => (
                              <option key={daybook.id} value={daybook.id}>
                                 {daybook.name}
                              </option>
                           ))}
                        </Select>
                     </Box>
                  </VStack>
               </ModalBody>

               <ModalFooter>
                  <Button variant="ghost" mr={3} onClick={handleActionClose}>
                     Cancel
                  </Button>
                  <Button colorScheme="green" onClick={handleSavePurchase} isDisabled={!selectedRowDaybook}>
                     Save Purchase
                  </Button>
               </ModalFooter>
            </ModalContent>
         </Modal>

         <SalesDetails
            isOpen={isOpenPurchaseReturnSingleView}
            onClose={handleSalesDetailsClose}
            singleEntityRights={purchaseInvoiceRight || purchaseReturnRight}
            title={'Purchase Invoice'}
            entityType={'purchaseinvoice'}
            singleEntity={viewDetail}
            handlePrevPage={handlePrevPage}
            handleNextPage={handleNextPage}
            setIsEditFormOpen={setIsEditFormOpen}
            setIsOpenSingleView={setIsOpenPurchaseReturnSingleView}
            isEditFormOpen={isEditFormOpen}
            // paginate={paginateData}
            viewDetail={viewDetail}
            lineItems={viewDetail?.lineitems}
            billTerms={viewDetail?.purchasereturnbillteams}
            onDelete={deletePurchaseReturn}
            handleSerchClose={() => console.log('')}
         />
      </Box>
   );
};

export default React.memo(GSTR2AB2BReconcilation);
